/**
 * Error Logging Utility
 * 
 * This utility provides enhanced error logging capabilities for the application.
 * It captures detailed error information and logs it to the console.
 * In a production environment, this could be extended to send logs to a monitoring service.
 */

interface ErrorLogDetails {
  component?: string;
  action?: string;
  status?: number;
  statusText?: string;
  data?: any;
  headers?: any;
  config?: any;
  message?: string;
  code?: string;
  stack?: string;
  timestamp?: string;
  userId?: number | string;
  additionalInfo?: Record<string, any>;
}

/**
 * Logs detailed error information to the console
 * Can be extended to send logs to a monitoring service
 */
export const logError = (error: any, context: Partial<ErrorLogDetails> = {}): void => {
  // Extract data from the error object
  const errorDetails: ErrorLogDetails = {
    component: context.component || 'Unknown Component',
    action: context.action || 'Unknown Action',
    timestamp: new Date().toISOString(),
    message: error.message,
    code: error.code,
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    headers: error.response?.headers,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      params: error.config?.params,
      data: error.config?.data ? JSON.parse(error.config.data) : null,
    },
    stack: error.stack,
    ...context.additionalInfo && { additionalInfo: context.additionalInfo }
  };

  // Add user ID if available from localStorage
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      errorDetails.userId = user.id;
    }
  } catch (e) {
    console.error('Error parsing user data from localStorage:', e);
  }

  // Log error details to console
  console.group(`📛 Error: ${errorDetails.component} - ${errorDetails.action}`);
  console.error('Error Details:', errorDetails);
  console.error('Original Error:', error);
  console.groupEnd();

  // In production, you could send this to a logging service
  // Example: sendToLogService(errorDetails);
};

/**
 * Creates an error message suitable for displaying to users
 */
export const getUserFriendlyErrorMessage = (error: any): string => {
  // Handle specific HTTP status codes
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return error.response.data?.error || 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please login again.';
      case 403:
        return 'You don\'t have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return error.response.data?.error || 'Validation failed. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'An internal server error occurred. Please try again later.';
      case 503:
        return 'The server is temporarily unavailable. Please try again later or contact support if the problem persists.';
      default:
        return error.response.data?.error || `Server error (${error.response.status}). Please try again later.`;
    }
  }

  // Handle network errors
  if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Handle timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request timed out. Please try again later.';
  }

  // Default error message
  return error.message || 'An unexpected error occurred. Please try again later.';
};

export default {
  logError,
  getUserFriendlyErrorMessage,
};
